import { CurrencyAmount, Fraction, TradeType } from '@kodiak-finance/sdk-core'
import JSBI from 'jsbi'

import { nativeOnChain } from '../../constants/tokens'
import { useCurrency, useToken } from '../../hooks/Tokens'
import {
  AddLiquidityAndStakeBaultSingleZapTransactionInfo,
  AddLiquidityAndStakeBaultTransactionInfo,
  AddLiquidityIslandSingleZapTransactionInfo,
  AddLiquidityIslandTransactionInfo,
  AddLiquidityV2DepositPageTransactionInfo,
  AddLiquidityV2PoolTransactionInfo,
  AddLiquidityV3PoolTransactionInfo,
  AddStakingIncentivesTransactionInfo,
  AirdropClaimTransactionInfo,
  ApproveTransactionInfo,
  ClaimFarmRewardsTransactionInfo,
  CollectFeesTransactionInfo,
  CreateV3PoolTransactionInfo,
  DeployFarmTransactionInfo,
  DepositIntoBaultTransactionInfo,
  DepositLiquidityStakingTransactionInfo,
  DividendsAllocateTransactionInfo,
  DividendsDeallocateTransactionInfo,
  DividendsHarvestTransactionInfo,
  ExactInputSwapTransactionInfo,
  ExactOutputSwapTransactionInfo,
  ExecuteTransactionInfo,
  FarmConfigUpdateInfo,
  FarmStartInfo,
  MintLiquidWrapperTransactionInfo,
  QueueTransactionInfo,
  RedeemFromBaultTransactionInfo,
  RedeemTransactionInfo,
  RemoveLiquidityIslandTransactionInfo,
  RemoveLiquidityV2TransactionInfo,
  RemoveLiquidityV3TransactionInfo,
  RewardVaultRewardClaimTransactionInfo,
  SendTokenTransactionInfo,
  StakeCancelRedeemTransactionInfo,
  StakeConvertTransactionInfo,
  StakeHarvestKdkTransactionInfo,
  StakeLiquidityIslandTransactionInfo,
  StakeRedeemTransactionInfo,
  SubmitProposalTransactionInfo,
  TokenMigrationTransactionInfo,
  TransactionInfo,
  TransactionType,
  UnstakeLiquidityIslandTransactionInfo,
  WithdrawLiquidityStakingTransactionInfo,
  WrapTransactionInfo,
} from '../../state/transactions/types'

function formatAmount(amountRaw: string, decimals: number, sigFigs: number): string {
  return new Fraction(amountRaw, JSBI.exponentiate(JSBI.BigInt(10), JSBI.BigInt(decimals))).toSignificant(sigFigs)
}

function FormattedCurrencyAmount({
  rawAmount,
  symbol,
  decimals,
  sigFigs,
}: {
  rawAmount: string
  symbol: string
  decimals: number
  sigFigs: number
}) {
  return (
    <>
      {formatAmount(rawAmount, decimals, sigFigs)} {symbol}
    </>
  )
}

function FormattedCurrencyAmountManaged({
  rawAmount,
  currencyId,
  sigFigs = 6,
}: {
  rawAmount: string
  currencyId: string
  sigFigs: number
}) {
  const currency = useCurrency(currencyId)
  return currency ? (
    <FormattedCurrencyAmount
      rawAmount={rawAmount}
      decimals={currency.decimals}
      sigFigs={sigFigs}
      symbol={currency.symbol ?? '???'}
    />
  ) : null
}

function SubmitProposalTransactionSummary(_: { info: SubmitProposalTransactionInfo }) {
  return <>Submit new proposal</>
}

function FarmDeploySummary({ info }: { info: DeployFarmTransactionInfo }) {
  return <>Deployed farm</>
}

function FarmConfigUpdateSummary({ info }: { info: FarmConfigUpdateInfo }) {
  const changes: string[] = []

  return <>Updated farm configuration</>
}

function FarmStartSummary({ info }: { info: FarmStartInfo }) {
  return <>Start farm</>
}

function ApprovalSummary({ info }: { info: ApproveTransactionInfo }) {
  const token = useToken(info.tokenAddress)

  return <>Approve {token?.symbol}</>
}

function QueueSummary({ info }: { info: QueueTransactionInfo }) {
  const proposalKey = `${info.governorAddress}/${info.proposalId}`
  return <>Queue proposal {proposalKey}.</>
}

function ExecuteSummary({ info }: { info: ExecuteTransactionInfo }) {
  const proposalKey = `${info.governorAddress}/${info.proposalId}`
  return <>Execute proposal {proposalKey}.</>
}

function WrapSummary({ info: { chainId, currencyAmountRaw, unwrapped } }: { info: WrapTransactionInfo }) {
  const native = chainId ? nativeOnChain(chainId) : undefined

  if (unwrapped) {
    return (
      <>
        Unwrap{' '}
        <FormattedCurrencyAmount
          rawAmount={currencyAmountRaw}
          symbol={native?.wrapped?.symbol ?? 'WETH'}
          decimals={18}
          sigFigs={6}
        />{' '}
        to {native?.symbol ?? 'ETH'}
      </>
    )
  } else {
    return (
      <>
        Wrap{' '}
        <FormattedCurrencyAmount
          rawAmount={currencyAmountRaw}
          symbol={native?.symbol ?? 'ETH'}
          decimals={18}
          sigFigs={6}
        />{' '}
        to {native?.wrapped?.symbol ?? 'WETH'}
      </>
    )
  }
}

function RedeemSummary({ info: { chainId, currencyAmountRaw } }: { info: RedeemTransactionInfo }) {
  const native = chainId ? nativeOnChain(chainId) : undefined

  return (
    <>
      Redeem <FormattedCurrencyAmount rawAmount={currencyAmountRaw} symbol="BGT" decimals={18} sigFigs={6} /> for{' '}
      {native?.symbol ?? 'ETH'}
    </>
  )
}

function DepositLiquidityStakingSummary(_: { info: DepositLiquidityStakingTransactionInfo }) {
  // not worth rendering the tokens since you can should no longer deposit liquidity in the staking contracts
  // todo: deprecate and delete the code paths that allow this, show user more information
  return <>Deposit liquidity</>
}

function WithdrawLiquidityStakingSummary(_: { info: WithdrawLiquidityStakingTransactionInfo }) {
  return <>Withdraw deposited liquidity</>
}

function CreateV3PoolSummary({ info: { quoteCurrencyId, baseCurrencyId } }: { info: CreateV3PoolTransactionInfo }) {
  const baseCurrency = useCurrency(baseCurrencyId)
  const quoteCurrency = useCurrency(quoteCurrencyId)

  return (
    <>
      Create {baseCurrency?.symbol}/{quoteCurrency?.symbol} V3 pool
    </>
  )
}

function CollectFeesSummary({ info: { currencyId0, currencyId1 } }: { info: CollectFeesTransactionInfo }) {
  const currency0 = useCurrency(currencyId0)
  const currency1 = useCurrency(currencyId1)

  return (
    <>
      Collect {currency0?.symbol}/{currency1?.symbol} fees
    </>
  )
}

function RemoveLiquidityV3Summary({
  info: { baseCurrencyId, quoteCurrencyId, expectedAmountBaseRaw, expectedAmountQuoteRaw },
}: {
  info: RemoveLiquidityV3TransactionInfo
}) {
  return (
    <>
      Remove{' '}
      <FormattedCurrencyAmountManaged rawAmount={expectedAmountBaseRaw} currencyId={baseCurrencyId} sigFigs={3} /> and{' '}
      <FormattedCurrencyAmountManaged rawAmount={expectedAmountQuoteRaw} currencyId={quoteCurrencyId} sigFigs={3} />
    </>
  )
}

function AddLiquidityV3PoolSummary({
  info: { createPool, quoteCurrencyId, baseCurrencyId },
}: {
  info: AddLiquidityV3PoolTransactionInfo
}) {
  const baseCurrency = useCurrency(baseCurrencyId)
  const quoteCurrency = useCurrency(quoteCurrencyId)

  return createPool ? (
    <>
      Create pool and add {baseCurrency?.symbol}/{quoteCurrency?.symbol} V3 liquidity
    </>
  ) : (
    <>
      Add {baseCurrency?.symbol}/{quoteCurrency?.symbol} V3 liquidity
    </>
  )
}

function RemoveLiquidityIslandSummary({
  info: { tokenA, tokenB, sharesBurned, tokenAAmount, tokenBAmount },
}: {
  info: RemoveLiquidityIslandTransactionInfo
}) {
  return (
    <>
      Withdraw <FormattedCurrencyAmountManaged rawAmount={tokenAAmount} currencyId={tokenA} sigFigs={3} /> and{' '}
      <FormattedCurrencyAmountManaged rawAmount={tokenBAmount} currencyId={tokenB} sigFigs={3} /> from Island
    </>
  )
}

function RemoveLiquidityV2Summary({
  info: { tokenA, tokenB, sharesBurned, tokenAAmount, tokenBAmount },
}: {
  info: RemoveLiquidityV2TransactionInfo
}) {
  return (
    <>
      Withdraw <FormattedCurrencyAmountManaged rawAmount={tokenAAmount} currencyId={tokenA} sigFigs={3} /> and{' '}
      <FormattedCurrencyAmountManaged rawAmount={tokenBAmount} currencyId={tokenB} sigFigs={3} /> from V2 Pool
    </>
  )
}

function AddLiquidityIslandSummary({
  info: { tokenASymbol, tokenBSymbol, expectedAmountBaseRaw },
}: {
  info: AddLiquidityIslandTransactionInfo
}) {
  return (
    <>
      Added Liquidity to {tokenASymbol}/{tokenBSymbol} Island
    </>
  )
}

function AddLiquidityIslandSingleZapSummary({ info }: { info: AddLiquidityIslandSingleZapTransactionInfo }) {
  return (
    <>
      Zapped {info.inputAmount} {info.inputTokenSymbol} into {info.tokenASymbol}/{info.tokenBSymbol} Island
    </>
  )
}

function AddLiquidityV2DepositPageSummary({
  info: { poolId, tokenASymbol, tokenBSymbol },
}: {
  info: AddLiquidityV2DepositPageTransactionInfo
}) {
  return (
    <>
      Added Liquidity to {tokenASymbol}/{tokenBSymbol} V2 Pool
    </>
  )
}

function AddLiquidityV2PoolSummary({
  info: { quoteCurrencyId, expectedAmountBaseRaw, expectedAmountQuoteRaw, baseCurrencyId },
}: {
  info: AddLiquidityV2PoolTransactionInfo
}) {
  return (
    <>
      Add <FormattedCurrencyAmountManaged rawAmount={expectedAmountBaseRaw} currencyId={baseCurrencyId} sigFigs={3} />{' '}
      and <FormattedCurrencyAmountManaged rawAmount={expectedAmountQuoteRaw} currencyId={quoteCurrencyId} sigFigs={3} />{' '}
      to Kodiak V2
    </>
  )
}

function SwapSummary({ info }: { info: ExactInputSwapTransactionInfo | ExactOutputSwapTransactionInfo }) {
  if (info.tradeType === TradeType.EXACT_INPUT) {
    return (
      <>
        Swap exactly{' '}
        <FormattedCurrencyAmountManaged
          rawAmount={info.inputCurrencyAmountRaw}
          currencyId={info.inputCurrencyId}
          sigFigs={6}
        />{' '}
        for{' '}
        <FormattedCurrencyAmountManaged
          rawAmount={info.expectedOutputCurrencyAmountRaw}
          currencyId={info.outputCurrencyId}
          sigFigs={6}
        />
      </>
    )
  } else {
    return (
      <>
        Swap{' '}
        <FormattedCurrencyAmountManaged
          rawAmount={info.expectedInputCurrencyAmountRaw}
          currencyId={info.inputCurrencyId}
          sigFigs={6}
        />{' '}
        for exactly{' '}
        <FormattedCurrencyAmountManaged
          rawAmount={info.outputCurrencyAmountRaw}
          currencyId={info.outputCurrencyId}
          sigFigs={6}
        />
      </>
    )
  }
}

function StakeLiquidityIslandTransactionSummary({ info }: { info: StakeLiquidityIslandTransactionInfo }) {
  const token = useToken(info.islandId)
  const amount = token ? CurrencyAmount.fromRawAmount(token, info.stakeAmount) : undefined

  return (
    <>
      Stake {amount?.toSignificant(6)} {token?.symbol}
    </>
  )
}

function UnstakeLiquidityIslandTransactionSummary({ info }: { info: UnstakeLiquidityIslandTransactionInfo }) {
  return <>Unstake {info.amount}</>
}

function ClaimFarmRewardsTransactionSummary({ info }: { info: ClaimFarmRewardsTransactionInfo }) {
  return <>Claim {info.rewards.map((item) => `${item.amount} ${item.token}`).join(', ')}</>
}

function RewardVaultRewardClaimTransactionSummary({ info }: { info: RewardVaultRewardClaimTransactionInfo }) {
  return (
    <>
      Claim {info.amount} {info.rewardTokenSymbol ? `${info.rewardTokenSymbol} rewards` : 'rewards'}
    </>
  )
}

function DepositIntoBaultSummary({ info }: { info: DepositIntoBaultTransactionInfo }) {
  return <>Deposit {info.amount} into Auto-Compound</>
}

function AddLiquidityAndStakeBaultSummary({ info }: { info: AddLiquidityAndStakeBaultTransactionInfo }) {
  const { amount, tokenAAmount, tokenBAmount, tokenASymbol, tokenBSymbol } = info
  return (
    <>
      Deposit {tokenAAmount} {tokenASymbol} and {tokenBAmount} {tokenBSymbol} into the island and staked {amount} to
      Baults
    </>
  )
}

function AddLiquidityAndStakeBaultSingleZapSummary({
  info,
}: {
  info: AddLiquidityAndStakeBaultSingleZapTransactionInfo
}) {
  return (
    <>
      Zapped {info.inputAmount} {info.inputTokenSymbol} into {info.tokenASymbol}/{info.tokenBSymbol} Island and staked{' '}
      {info.outputAmount} to Baults
    </>
  )
}

function RedeemFromBaultSummary({ info }: { info: RedeemFromBaultTransactionInfo }) {
  return <>Redeem {info.amount} from Auto-Compound</>
}

function MintLiquidWrapperTransactionSummary({ info }: { info: MintLiquidWrapperTransactionInfo }) {
  return (
    <>
      Mint {info.amount} {info.tokenSymbol}
    </>
  )
}

function StakeConvertTransactionSummary({ info }: { info: StakeConvertTransactionInfo }) {
  return (
    <>
      {info.isStake ? 'Convert and Stake' : 'Convert'} {info.amount}
    </>
  )
}

function StakeRedeemTransactionSummary({ info }: { info: StakeRedeemTransactionInfo }) {
  return <>Redeem {info.amount}</>
}

function DividendsAllocateTransactionSummary({ info }: { info: DividendsAllocateTransactionInfo }) {
  return <>Stake {info.amount}</>
}

function DividendsDeallocateTransactionSummary({ info }: { info: DividendsDeallocateTransactionInfo }) {
  return <>Unstake {info.amount}</>
}

function DividendsHarvestTransactionSummary(props: { info: DividendsHarvestTransactionInfo }) {
  return <>Harvest Rewards</>
}

function StakeHarvestKdkTransactionSummary({ info }: { info: StakeHarvestKdkTransactionInfo }) {
  return <>Redeem {info.amount}</>
}

function StakeCancelRedeemTransactionSummary({ info }: { info: StakeCancelRedeemTransactionInfo }) {
  return <>Cancel redeem {info.amount}</>
}

function AddStakingIncentivesTransactionSummary({ info }: { info: AddStakingIncentivesTransactionInfo }) {
  return <>Add Incentives {info.amount}</>
}

function SendTokenTransactionSummary({ info }: { info: SendTokenTransactionInfo }) {
  return (
    <>
      Sent {info.amount} {info.tokenSymbol}
    </>
  )
}

function TokenMigrationTransactionSummary({ info }: { info: TokenMigrationTransactionInfo }) {
  const fromToken = useToken(info.fromToken)
  const toToken = useToken(info.toToken)

  return (
    <>
      Migrate{' '}
      {fromToken && info.amount ? (
        <FormattedCurrencyAmount
          rawAmount={info.amount}
          decimals={fromToken.decimals}
          sigFigs={6}
          symbol={fromToken.symbol ?? '???'}
        />
      ) : (
        `${info.amount} tokens`
      )}{' '}
      → {toToken?.symbol || 'xKDK'}
    </>
  )
}

function AirdropClaimTransactionSummary({ info }: { info: AirdropClaimTransactionInfo }) {
  const token = useToken(info.token)

  return (
    <>
      Claim{' '}
      <FormattedCurrencyAmount
        rawAmount={info.amount}
        decimals={token?.decimals ?? 18}
        sigFigs={6}
        symbol={token?.symbol ?? 'xKDK'}
      />
    </>
  )
}

export function TransactionSummary({ info }: { info: TransactionInfo }) {
  switch (info.type) {
    case TransactionType.CANCEL_SPOT_ORDER:
      return <>Cancel order</>

    case TransactionType.ADD_LIQUIDITY_V3_POOL:
      return <AddLiquidityV3PoolSummary info={info} />

    case TransactionType.ADD_LIQUIDITY_ISLAND:
      return <AddLiquidityIslandSummary info={info} />

    case TransactionType.ADD_LIQUIDITY_ISLAND_SINGLE_ZAP:
      return <AddLiquidityIslandSingleZapSummary info={info} />

    case TransactionType.ADD_LIQUIDITY_V2_DEPOSIT_PAGE:
      return <AddLiquidityV2DepositPageSummary info={info} />

    case TransactionType.ADD_LIQUIDITY_V2_POOL:
      return <AddLiquidityV2PoolSummary info={info} />

    case TransactionType.DEPOSIT_LIQUIDITY_STAKING:
      return <DepositLiquidityStakingSummary info={info} />

    case TransactionType.WITHDRAW_LIQUIDITY_STAKING:
      return <WithdrawLiquidityStakingSummary info={info} />

    case TransactionType.SWAP:
      return <SwapSummary info={info} />

    case TransactionType.APPROVAL:
      return <ApprovalSummary info={info} />

    case TransactionType.WRAP:
      return <WrapSummary info={info} />

    case TransactionType.REDEEM:
      return <RedeemSummary info={info} />

    case TransactionType.FARM_CONFIG_UPDATE:
      return <FarmConfigUpdateSummary info={info} />

    case TransactionType.FARM_START:
      return <FarmStartSummary info={info} />

    case TransactionType.CREATE_V3_POOL:
      return <CreateV3PoolSummary info={info} />

    case TransactionType.COLLECT_FEES:
      return <CollectFeesSummary info={info} />

    case TransactionType.REMOVE_LIQUIDITY_V3:
      return <RemoveLiquidityV3Summary info={info} />

    case TransactionType.FARM_DEPLOYMENT:
      return <FarmDeploySummary info={info} />

    case TransactionType.REMOVE_LIQUIDITY_ISLAND:
      return <RemoveLiquidityIslandSummary info={info} />

    case TransactionType.REMOVE_LIQUIDITY_V2:
      return <RemoveLiquidityV2Summary info={info} />

    case TransactionType.QUEUE:
      return <QueueSummary info={info} />

    case TransactionType.EXECUTE:
      return <ExecuteSummary info={info} />

    case TransactionType.SUBMIT_PROPOSAL:
      return <SubmitProposalTransactionSummary info={info} />

    case TransactionType.STAKE_LIQUIDITY_ISLAND:
      return <StakeLiquidityIslandTransactionSummary info={info} />

    case TransactionType.CLAIM_FARM_REWARDS:
      return <ClaimFarmRewardsTransactionSummary info={info} />

    case TransactionType.REWARD_VAULT_REWARD_CLAIM:
      return <RewardVaultRewardClaimTransactionSummary info={info} />

    case TransactionType.DEPOSIT_INTO_BAULT:
      return <DepositIntoBaultSummary info={info} />

    case TransactionType.ADD_LIQUIDITY_AND_STAKE_BAULT:
      return <AddLiquidityAndStakeBaultSummary info={info} />

    case TransactionType.ADD_LIQUIDITY_AND_STAKE_BAULT_SINGLE_ZAP:
      return <AddLiquidityAndStakeBaultSingleZapSummary info={info} />

    case TransactionType.REDEEM_FROM_BAULT:
      return <RedeemFromBaultSummary info={info} />

    case TransactionType.MINT:
      return <MintLiquidWrapperTransactionSummary info={info} />

    case TransactionType.UNSTAKE_LIQUIDITY_ISLAND:
      return <UnstakeLiquidityIslandTransactionSummary info={info} />

    case TransactionType.STAKE_CONVERT:
      return <StakeConvertTransactionSummary info={info} />

    case TransactionType.STAKE_REDEEM:
      return <StakeRedeemTransactionSummary info={info} />

    case TransactionType.DIVIDENDS_ALLOCATE:
      return <DividendsAllocateTransactionSummary info={info} />

    case TransactionType.DIVIDENDS_DEALLOCATE:
      return <DividendsDeallocateTransactionSummary info={info} />

    case TransactionType.DIVIDENDS_HARVEST:
      return <DividendsHarvestTransactionSummary info={info} />

    case TransactionType.STAKE_HARVEST_KDK:
      return <StakeHarvestKdkTransactionSummary info={info} />

    case TransactionType.STAKE_CANCEL_REDEEM:
      return <StakeCancelRedeemTransactionSummary info={info} />

    case TransactionType.ADD_STAKING_INCENTIVES:
      return <AddStakingIncentivesTransactionSummary info={info} />

    case TransactionType.TOKEN_MIGRATION:
      return <TokenMigrationTransactionSummary info={info} />

    case TransactionType.AIRDROP_CLAIM:
      return <AirdropClaimTransactionSummary info={info} />

    case TransactionType.SEND_TOKEN:
      return <SendTokenTransactionSummary info={info} />
  }
}
